import React, { useMemo, useState } from 'react';
import { LiveGuidelineCriterion } from '../../../../../../redux/actions/liveGuidelineActions/types';
import { formatLiveGuidelineCriteria } from '../../../../../../helpers/liveGuidelineCriteria';
import { useMasterFormatTree } from '../../../../../../hooks/masterFormat';
import { setLiveGuidelineCookie } from '../../../../../../redux/persistedState';

import {
    CriteriaContainer,
    CriteriaHeader,
    CriteriaTitle,
    CriteriaGrid,
    CriteriaColumn,
    CriteriaGroup,
    CriteriaGroupTitle,
    CriteriaItem,
    CriteriaItemLabel,
    CriteriaEmpty,
} from './LiveGuidelineCriteriaBanner.styles';
import Switcher from '../../../../../../components/common/switcher/Switcher';
import { blueDark02 } from '../../../../../../styles/colors';
import { PrimaryButton } from '../../../../../../styles/common.styles';

interface Props {
    criteria?: LiveGuidelineCriterion[];
    onEditCriteria?: () => void;
    canManageGuidelines?: () => boolean;
}

const LiveGuidelineCriteria: React.FC<Props> = ({ criteria = [], onEditCriteria, canManageGuidelines }) => {
    const [open, setOpen] = useState(false);

    const masterFormatTree = useMasterFormatTree();

    const groupedCriteria = useMemo(() => {
        return formatLiveGuidelineCriteria(criteria, masterFormatTree);
    }, [criteria, masterFormatTree]);

    // Same reading order as before (row by row across 3 columns), but each column stacks on its own.
    const columns = useMemo(() => {
        const result: (typeof groupedCriteria)[] = [[], [], []];
        groupedCriteria.forEach((group, index) => result[index % 3].push(group));
        return result;
    }, [groupedCriteria]);

    return (
        <CriteriaContainer>
            <CriteriaHeader onClick={() => setOpen((previous) => !previous)}>
                <Switcher
                    width={20}
                    height={20}
                    clicked={open}
                    stopPropagation={false}
                    iconColor={blueDark02}
                    iconColorActive={'#D0E3E7'}
                    bgColorActive={blueDark02}
                    bgColor={'transparent'}
                />
                <CriteriaTitle>Live Guideline criteria</CriteriaTitle>
            </CriteriaHeader>

            {open && (
                <>
                    {canManageGuidelines?.() && onEditCriteria && (
                        <PrimaryButton
                            style={
                                { 
                                    margin: '0 32px 4px 32px',
                                    width: '99px',
                                    height: '32px',
                                }
                            }
                            onClick={(event) => {
                                event.stopPropagation();
                                setLiveGuidelineCookie(true);
                                onEditCriteria();
                            }}
                        >
                            Edit criteria
                        </PrimaryButton>
                    )}

                    {!groupedCriteria.length ? (
                        <CriteriaEmpty>No criteria selected</CriteriaEmpty>
                    ) : (
                        <CriteriaGrid>
                            {columns.map((columnGroups, columnIndex) => (
                                <CriteriaColumn key={columnIndex}>
                                    {columnGroups.map((group) => (
                                        <CriteriaGroup key={group.title}>
                                            <CriteriaGroupTitle>{group.title}</CriteriaGroupTitle>

                                            {group.entries.map((entry, index) => (
                                                <CriteriaItem
                                                    key={`${entry.label ?? entry.value}-${index}`}
                                                >
                                                    {entry.label && (
                                                        <CriteriaItemLabel>
                                                            {entry.label}:{' '}
                                                        </CriteriaItemLabel>
                                                    )}
                                                    {entry.value}
                                                </CriteriaItem>
                                            ))}
                                        </CriteriaGroup>
                                    ))}
                                </CriteriaColumn>
                            ))}
                        </CriteriaGrid>
                    )}
                </>
            )}
        </CriteriaContainer>
    );
};

export default LiveGuidelineCriteria;
